/**
 * The Quotes list (docs/features/order-to-invoice/40_quotes_module_spec.md
 * §10): CIS quotes only, `QT-` numbers; Tharstern's estimates are their own
 * read-only page. Status, search and sort live in the URL, so a filtered
 * list is a link; they run on the server (`listQuotes`), and this never
 * filters the response (docs/list-endpoints.md). Changing any of them goes
 * back to page 1.
 *
 * Self-loading: the Quotes page renders it across every customer in the
 * caller's scope, and the customer's Quotes tab renders it fixed to one (by
 * the Tharstern account code the customer page is keyed on).
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { ListResponse, QuoteListOptions } from "@shared-core/data/adapter";
import type { QuoteListItem, QuoteStatus } from "@shared-core/data/quote-types";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Input } from "../../primitives/input";
import { Skeleton } from "../../primitives/skeleton";
import { ListPagination } from "../../patterns/ListPagination";
import { QuoteStatusPill } from "./QuotePanels";

const short = formatShortDateOrDash;

/**
 * The filter's own choices. A filter is offered before any row arrives, so
 * these are the list's static copy; every row's label is the backend's.
 */
const STATUS_FILTERS: Array<{ value: QuoteStatus; label: string }> = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "accepted", label: "Accepted" },
  { value: "declined", label: "Declined" },
  { value: "expired", label: "Expired" },
  { value: "withdrawn", label: "Withdrawn" },
];
const SORTS: Array<{ value: string; label: string }> = [
  { value: "-published_at", label: "Newest published" },
  { value: "expires_at", label: "Expiring soonest" },
  { value: "-total_pence", label: "Highest value" },
  { value: "customer_name", label: "Customer A–Z" },
  { value: "-quote_no", label: "Quote number" },
];
const STATUSES = STATUS_FILTERS.map((f) => f.value);
const SORT_VALUES = SORTS.map((s) => s.value);

function oneOf<T extends string>(
  value: string | null,
  allowed: T[],
): T | undefined {
  return allowed.includes(value as T) ? (value as T) : undefined;
}

const selectClass = "h-9 rounded-md border border-border bg-card px-3 text-sm";

/** Where the quote stands: when it expires, or how it closed (and what followed). */
function WhereItStands({ row }: { row: QuoteListItem }) {
  if (row.status === "draft") return <>Private to staff</>;
  if (row.status === "published") return <>Expires {short(row.expires_at)}</>;
  if (row.status === "accepted" && row.order_no)
    return (
      <>
        {short(row.decided_at)} →{" "}
        <Link
          to={paths.orderDetail({ orderNo: row.order_no })}
          className="underline-offset-2 hover:underline"
        >
          {row.order_no}
        </Link>
      </>
    );
  if (row.replaced_by)
    return (
      <Link
        to={paths.quoteDetail({ quoteNo: row.replaced_by.quote_no })}
        className="underline-offset-2 hover:underline"
      >
        {row.replaced_by.label}
      </Link>
    );
  return <>{short(row.decided_at)}</>;
}

export function QuotesList({
  customerKey,
}: {
  /** Fixes the list to one customer, by its page key (Tharstern code, else `CUS-` code): their Quotes tab. */
  customerKey?: string;
}) {
  const adapter = useDataAdapter();
  const [params, setParams] = useSearchParams();
  const status = oneOf(params.get("status"), STATUSES);
  const sort = oneOf(params.get("sort"), SORT_VALUES);
  const q = params.get("q") || undefined;
  const page = Number(params.get("page")) || 1;
  const isFiltered = !!(status || q);

  const [result, setResult] = useState<ListResponse<QuoteListItem> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const options: QuoteListOptions = {
      customerKey,
      status: status ? [status] : undefined,
      sort,
      q,
      page,
    };
    setError(null);
    setResult(null);
    adapter
      .listQuotes(options)
      .then((r) => !cancelled && setResult(r))
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Could not load quotes.");
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, customerKey, status, sort, q, page]);

  const setFilter = (key: string, value: string) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== "page") next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          // Uncontrolled, so remount on a new q: back and forward show the URL's search.
          key={q ?? ""}
          type="search"
          placeholder="Search quotes"
          aria-label="Search quotes"
          defaultValue={q ?? ""}
          onKeyDown={(e) => {
            if (e.key === "Enter") setFilter("q", e.currentTarget.value.trim());
          }}
          className="w-full sm:w-56"
        />
        <select
          aria-label="Status"
          value={status ?? ""}
          onChange={(e) => setFilter("status", e.target.value)}
          className={selectClass}
        >
          <option value="">Any status</option>
          {STATUS_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Sort"
          value={sort ?? SORTS[0]!.value}
          onChange={(e) =>
            setFilter(
              "sort",
              e.target.value === SORTS[0]!.value ? "" : e.target.value,
            )
          }
          className={selectClass}
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>Could not load quotes: {error}</AlertDescription>
        </Alert>
      )}
      {!result && !error && (
        <div className="space-y-2" aria-label="Loading quotes">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )}
      {result && result.items.length === 0 && (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {isFiltered
            ? "No quotes match these filters."
            : "No quotes yet. Quotes start as draft quotes in One."}
        </p>
      )}
      {result && result.items.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="text-left">
                  <th className="py-1.5 pr-3 font-normal">Quote</th>
                  {!customerKey && (
                    <th className="py-1.5 pr-3 font-normal">Customer</th>
                  )}
                  <th className="py-1.5 pr-3 font-normal">What</th>
                  <th className="py-1.5 pr-3 font-normal">Published</th>
                  <th className="py-1.5 pr-3 font-normal">Status</th>
                  <th className="py-1.5 pr-3 font-normal">Where it stands</th>
                  <th className="py-1.5 text-right font-normal">Value</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((row) => (
                  <tr
                    key={row.quote_no}
                    className="border-t border-border align-top"
                  >
                    <td className="py-2 pr-3 font-medium tabular-nums">
                      <Link
                        to={paths.quoteDetail({ quoteNo: row.quote_no })}
                        className="text-[color:var(--color-primary)] hover:underline"
                      >
                        {row.quote_no}
                      </Link>
                    </td>
                    {!customerKey && (
                      <td className="py-2 pr-3">
                        <Link
                          to={paths.customerDetail({
                            customerId:
                              row.customer.tharstern_customer_code ??
                              row.customer.customer_code,
                          })}
                          className="hover:underline"
                        >
                          {row.customer.name}
                        </Link>
                      </td>
                    )}
                    <td className="min-w-48 py-2 pr-3">
                      <span className="block">{row.title}</span>
                      <span className="block text-xs text-muted-foreground">
                        {[
                          `${row.line_count} ${row.line_count === 1 ? "line" : "lines"}`,
                          row.has_alternatives && "with choices",
                          `to ${row.contact_name}`,
                          row.replaces?.label,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-2 pr-3 tabular-nums">
                      {row.published_at ? short(row.published_at) : "Draft"}
                      <span className="block text-xs text-muted-foreground">
                        {row.published_by_name ?? row.created_by}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <QuoteStatusPill
                        status={row.status}
                        label={row.status_label}
                      />
                    </td>
                    <td className="whitespace-nowrap py-2 pr-3 text-muted-foreground">
                      <WhereItStands row={row} />
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {formatQuotedPrice(row.total_pence)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListPagination
            page={result.page}
            totalPages={result.totalPages}
            onPageChange={(p) => setFilter("page", String(p))}
          />
        </>
      )}
    </div>
  );
}
